import * as React from "react";
import {
  Link,
  createFileRoute,
  stripSearchParams,
  useNavigate,
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  CheckIcon,
  ChevronsUpDownIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { HTTPError } from "ky";
import type { ColumnDef } from "@tanstack/react-table";
import type { Group, GroupFormData } from "@/lib/queries/groups";

import type { User } from "@/lib/queries/users";
import { api } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  groupFormSchema,
  groupPoliciesQueryOptions,
  groupUsersListQueryOptions,
  groupsListQueryOptions,
} from "@/lib/queries/groups";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { generateDefaultValue } from "@/lib/utils/zod";
import { useDebouncedSearch } from "@/lib/hooks";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import { allPoliciesQueryOptions } from "@/lib/queries/policies";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { userQueryOptions, usersListQueryOptions } from "@/lib/queries/users";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Actions } from "@/components/ui/actions";
import { useResourceAccessPermission } from "@/lib/perms";
import {
  parseGatewayKeyConflict,
  savePendingPolicyConflict,
} from "@/lib/policy-conflicts";

const searchSchema = z.object({
  page: z.number().int().positive().default(1),
  per_page: z.number().int().positive().max(100).default(10),
  search: z.string().optional(),
  beans: z.boolean().optional(),
});

export const Route = createFileRoute("/_app/groups")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams({ search: "" })],
  },
  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient
      .ensureQueryData(groupsListQueryOptions(deps))
      .then((groups) =>
        groups.items.map((g) =>
          queryClient.ensureQueryData(groupPoliciesQueryOptions(g.id)),
        ),
      );
    queryClient.ensureQueryData(allPoliciesQueryOptions());
  },
  staticData: {
    permissionGuards: [
      {
        resource: "groups",
      },
    ],
  },
});

const columns = [
  {
    accessorKey: "name",
    header: "Name",
    size: 400,
  },
  {
    accessorKey: "description",
    header: "Description",
    size: 400,
  },
  {
    id: "actions",
    header: "Actions",
    maxSize: 240,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canView = useResourceAccessPermission("groups", ["read"]);
      const canEdit = useResourceAccessPermission("groups", ["update"]);
      const canDelete = useResourceAccessPermission("groups", ["delete"]);

      return (
        <Actions>
          {canView.data && (
            <Button
              size="lg"
              variant="ghost"
              className="w-full justify-start text-base"
              asChild
            >
              <Link to="/groups/$id" params={{ id: original.id }}>
                <EyeIcon className="size-5" />
                View
              </Link>
            </Button>
          )}
          {canEdit.data && (
            <EditDialog asChild group={original}>
              <Button
                size="lg"
                variant="ghost"
                className="w-full justify-start text-base"
              >
                <PencilIcon className="size-5" />
                Edit
              </Button>
            </EditDialog>
          )}
          {canDelete.data && <DeleteDialog group={original} />}
        </Actions>
      );
    },
  },
] as const satisfies Array<ColumnDef<Group>>;

function RouteComponent() {
  const searchParams = Route.useSearch();

  const [searchQuery, setSearchQuery, rawSearchQuery] = useDebouncedSearch(
    Route.id,
    "search",
  );

  const groups = useQuery(
    groupsListQueryOptions({ ...searchParams, search: searchQuery }),
  );

  const canView = useResourceAccessPermission("groups", ["read"]);
  const canEdit = useResourceAccessPermission("groups", ["update"]);
  const canDelete = useResourceAccessPermission("groups", ["delete"]);
  const canCreate = useResourceAccessPermission("groups", ["create"]);

  const showActions = Boolean(canView.data || canEdit.data || canDelete.data);

  const table = useReactTable({
    data: groups.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
      columnVisibility: {
        actions: showActions,
      },
    },
    rowCount: groups.data?.count ?? 1,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    enableColumnResizing: false,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Groups</h1>
        {canCreate.data && (
          <CreateDialog asChild>
            <Button>
              <PlusIcon />
              Create Group
            </Button>
          </CreateDialog>
        )}
      </div>

      <div className="relative min-w-[220px] flex-1 mb-4">
        <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search all columns..."
          value={rawSearchQuery ?? ""}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={groups.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

function DeleteDialog({ group }: { group: Group }) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn(id: string) {
      return api.raw.delete(`api/v1/groups/${id}`);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["groups", "list"] });
      queryClient.removeQueries({ queryKey: ["groups", "detail", group.id] });

      setOpen(false);
      toast.success(`Group "${group.name}" deleted successfully`);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body.detail) message = body.detail;
      }
      toast.error(`Delete failed: ${message}`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
        >
          <TrashIcon className="size-5" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-xs p-4 gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Group</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the group "{group.name}"? This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending} asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>

          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(group.id)}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({
  children,
  group,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; group: Group }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn({ id, data }: { data: GroupFormData; id: string }) {
      return api.raw.patch(`api/v1/groups/${id}`, { json: data }).json<Group>();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["groups", "list"] });
      queryClient.setQueryData(["groups", "detail", data.id], data);

      setOpen(false);
      toast.success(`Group "${group.name}" updated successfully`);
    },
    async onError(err, variables) {
      const conflict = await parseGatewayKeyConflict(err);
      if (conflict) {
        savePendingPolicyConflict({
          returnUrl: window.location.pathname + window.location.search,
          apiRequest: {
            method: "PATCH",
            url: `api/v1/groups/${variables.id}`,
            payload: variables.data as unknown as Record<string, unknown>,
            kind: "update-group",
            label: variables.data.name,
          },
          successToast: `Group "${variables.data.name}" updated successfully`,
          conflict,
        });
        setOpen(false);
        navigate({ to: "/resolve-conflicts" });
        return;
      }

      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body.detail) message = body.detail;
      }
      toast.error(`Edit failed: ${message}`);
    },
  });

  const policies = useQuery(groupPoliciesQueryOptions(group.id));
  const users = useQuery(
    // FIXME: will have to either change it to paginated table or get unpaginated endpoint from backend
    groupUsersListQueryOptions(group.id, { page: 1, per_page: 100 }),
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Group</DialogTitle>
          {policies.isSuccess && users.isSuccess ? (
            <GroupForm
              defaultValue={{
                ...group,
                policies: policies.data.map((p) => p.id),
                users: users.data.items.map((u) => u.user.id),
              }}
              disabled={mutation.isPending}
              buttonText={mutation.isPending ? "Saving..." : "Update"}
              onSubmit={(data) => mutation.mutate({ id: group.id, data })}
              onCancel={() => setOpen(false)}
            />
          ) : (
            "Loading..."
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  children,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async (data: GroupFormData) => {
      const response = await api.raw
        .post("api/v1/groups/", { json: data })
        .json<Group>();
      return response;
    },
    onSuccess(data) {
      queryClient.invalidateQueries({ queryKey: ["groups", "list"] });
      queryClient.setQueryData(["groups", "detail", data.id], data);

      setOpen(false);
      toast.success(`Group "${data.name}" created successfully`);
    },
    async onError(err, variables) {
      const conflict = await parseGatewayKeyConflict(err);
      if (conflict) {
        savePendingPolicyConflict({
          returnUrl: window.location.pathname + window.location.search,
          apiRequest: {
            method: "POST",
            url: "api/v1/groups/",
            payload: variables as unknown as Record<string, unknown>,
            kind: "create-group",
            label: variables.name,
          },
          successToast: `Group "${variables.name}" created successfully`,
          conflict,
        });
        setOpen(false);
        navigate({ to: "/resolve-conflicts" });
        return;
      }

      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body.detail) message = body.detail;
      }
      toast.error(`Create failed: ${message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Group</DialogTitle>
          <GroupForm
            disabled={mutation.isPending}
            buttonText={mutation.isPending ? "Saving..." : "Create"}
            onSubmit={mutation.mutate}
            onCancel={() => setOpen(false)}
          />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function GroupForm({
  disabled,
  buttonText,
  defaultValue,
  onSubmit,
  onCancel,
}: {
  disabled?: boolean;
  defaultValue?: GroupFormData;
  buttonText: React.ReactNode;
  onSubmit: (data: GroupFormData) => void;
  onCancel?: () => void;
}) {
  const form = useForm({
    resolver: zodResolver(groupFormSchema),
    defaultValues: {
      ...generateDefaultValue(groupFormSchema),
      ...defaultValue,
    },
  });

  const policies = useQuery(allPoliciesQueryOptions());

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter group name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  className="max-h-55"
                  placeholder="Enter group description"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="users"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Users</FormLabel>
              <FormControl>
                <UserSelectCombobox
                  value={field.value ?? []}
                  onValueChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="policies"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Policies</FormLabel>
              <FormControl>
                <div className="flex max-h-30 w-full flex-wrap gap-1 overflow-x-scroll rounded-lg border p-3">
                  {policies.isSuccess ? (
                    policies.data.map((e) => {
                      const enabled = field.value?.includes(e.id);
                      return (
                        <Badge
                          key={e.id}
                          className="py-0.5 transition-colors dark:border-gray-500/50"
                          variant={enabled ? "default" : "outline"}
                          onClick={() => {
                            // field.onChange doesn't work on first click
                            const currentValue = form.getValues("policies");

                            if (enabled)
                              form.setValue(
                                field.name,
                                currentValue?.filter((old) => old !== e.id),
                              );
                            else
                              form.setValue(field.name, [
                                ...(currentValue ?? []),
                                e.id,
                              ]);
                          }}
                        >
                          {e.name}
                          {enabled ? <XIcon /> : <PlusIcon />}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="flex items-center gap-1">
                      <Spinner />
                      Loading...
                    </span>
                  )}
                </div>
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}

          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function UserSelectCombobox({
  value,
  onValueChange,
}: {
  value: Array<User["id"]>;
  onValueChange: (newVal: Array<User["id"]>) => void;
}) {
  const [open, setOpen] = React.useState(false);

  const selectedUsers = useQueries({
    queries: value.map((id) => userQueryOptions(id)),
    combine: (result) =>
      Object.fromEntries(
        result
          .filter((q) => q.isSuccess)
          .map((user) => [user.data.id, user.data] as const),
      ),
  });

  const [rawSearch, setSearch] = React.useState("");
  const [search] = useDebouncedValue(rawSearch, {
    wait: 200,
  });

  const users = useQuery({
    ...usersListQueryOptions({
      page: 1,
      per_page: 50,
      search,
    }),
    placeholderData: keepPreviousData,
  });

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto max-w-full justify-between"
        >
          <div className="flex max-h-30 w-full flex-wrap items-center gap-1 overflow-x-scroll">
            {value.length > 0
              ? Object.values(selectedUsers).map((u) => (
                  <Badge key={u.id}>{u.full_name}</Badge>
                ))
              : "Select users..."}

            {value.length > Object.keys(selectedUsers).length && <Spinner />}
          </div>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command className="max-h-80">
          <CommandInput
            placeholder="Search department..."
            value={rawSearch}
            onValueChange={setSearch}
          />

          {users.isSuccess ? (
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {users.data.items.map((user) => {
                  const selected = value.includes(user.id);
                  return (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      keywords={[user.full_name]}
                      onSelect={() => {
                        if (value.includes(user.id))
                          onValueChange(value.filter((o) => o !== user.id));
                        else onValueChange([...value, user.id]);
                      }}
                    >
                      {user.full_name}
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          selected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          ) : (
            <Spinner className="m-4 self-center" />
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
